import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrganizationService } from 'src/app/@shared/services/organization.service';
import {
  PlainVersionSearchItem,
  VersionAttributeFilterPair,
  VersionAttributeSearchApiItem,
  VersionAttributeSearchResult,
  mapPlainVersionsToAttributeSearchResults,
  mapVersionsToAttributeSearchResults,
} from '../models/store-attribute.model';
import { normalizeODataCollection, extractApiErrorMessage } from './version.service';

export type VersionAssociationEntityType = 'offer' | 'promo' | 'offerGroup';

export interface VersionAssociationFilterContext {
  entityType: VersionAssociationEntityType;
  entityId: string;
  eventId: string;
}

@Injectable({
  providedIn: 'root',
})
export class VersionAssociationFilterService {
  private contextBehaviorSubject = new BehaviorSubject<VersionAssociationFilterContext | null>(null);
  private attributeFilterPairsBehaviorSubject = new BehaviorSubject<VersionAttributeFilterPair[]>([]);
  private versionCodeSearchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<number>(0);
  private searchRequestedBehaviorSubject = new BehaviorSubject<boolean>(false);
  private attributeSearchLoadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private attributeSearchErrorBehaviorSubject = new BehaviorSubject<string | null>(null);

  public attributeSearchLoading$ = this.attributeSearchLoadingBehaviorSubject.asObservable();
  public attributeSearchError$ = this.attributeSearchErrorBehaviorSubject.asObservable();

  private attributeSearchParams$ = combineLatest([
    this.contextBehaviorSubject,
    this.attributeFilterPairsBehaviorSubject,
    this.versionCodeSearchBehaviorSubject,
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
  );

  private attributeSearchResponse$ = this.attributeSearchParams$.pipe(
    tap(() => {
      this.attributeSearchLoadingBehaviorSubject.next(true);
      this.attributeSearchErrorBehaviorSubject.next(null);
    }),
    switchMap(([context, attributeFilters, versionCodeSearch]) => {
      const trimmedSearch = (versionCodeSearch ?? '').trim();
      const searchRequested = this.searchRequestedBehaviorSubject.value;

      if (!context?.eventId || !context.entityId) {
        this.attributeSearchLoadingBehaviorSubject.next(false);
        return of({ results: [] as VersionAttributeSearchResult[], total: 0 });
      }

      if (!attributeFilters.length && !trimmedSearch && !searchRequested) {
        this.attributeSearchLoadingBehaviorSubject.next(false);
        return of({ results: [] as VersionAttributeSearchResult[], total: 0 });
      }

      if (!attributeFilters.length) {
        return this.searchByVersionCodeOnly(context, trimmedSearch);
      }

      const baseUrl = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions`;
      const url = this.resolveAttributeFilterSearchUrl(baseUrl, context.entityType);
      const body = this.buildRequestBody(context, attributeFilters, versionCodeSearch);

      return this.httpClient.post<unknown>(url, body).pipe(
        map((res) => {
          const { items, total } = normalizeODataCollection<VersionAttributeSearchApiItem>(res);
          return {
            results: mapVersionsToAttributeSearchResults(items),
            total,
          };
        }),
        catchError((error) => {
          this.attributeSearchErrorBehaviorSubject.next(
            extractApiErrorMessage(error, 'Error searching versions by attribute filters'),
          );
          return of({ results: [] as VersionAttributeSearchResult[], total: 0 });
        }),
      );
    }),
    tap(() => this.attributeSearchLoadingBehaviorSubject.next(false)),
    shareReplay(1),
  );

  public attributeSearchResults$: Observable<VersionAttributeSearchResult[]> =
    this.attributeSearchResponse$.pipe(map((res) => res.results));

  public attributeSearchTotal$: Observable<number> =
    this.attributeSearchResponse$.pipe(map((res) => res.total));

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService,
  ) {}

  setContext(context: VersionAssociationFilterContext): void {
    this.contextBehaviorSubject.next(context);
  }

  setAttributeFilterPairs(pairs: VersionAttributeFilterPair[]): void {
    this.attributeFilterPairsBehaviorSubject.next(pairs);
  }

  setVersionCodeSearch(search: string): void {
    this.versionCodeSearchBehaviorSubject.next(search ?? '');
  }

  reload(): void {
    this.searchRequestedBehaviorSubject.next(true);
    this.reloadBehaviorSubject.next(this.reloadBehaviorSubject.value + 1);
  }

  reset(): void {
    this.searchRequestedBehaviorSubject.next(false);
    this.attributeFilterPairsBehaviorSubject.next([]);
    this.versionCodeSearchBehaviorSubject.next('');
    this.reloadBehaviorSubject.next(0);
    this.attributeSearchErrorBehaviorSubject.next(null);
  }

  private searchByVersionCodeOnly(
    context: VersionAssociationFilterContext,
    search: string,
  ): Observable<{ results: VersionAttributeSearchResult[]; total: number }> {
    const baseUrl = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions`;
    const url = this.resolveVersionCodeSearchUrl(baseUrl, context.entityType);
    const params = this.buildVersionCodeSearchParams(context, search);

    return this.httpClient.get<unknown>(url, { params }).pipe(
      map((res) => {
        const { items, total } = normalizeODataCollection<PlainVersionSearchItem>(res);
        return {
          results: mapPlainVersionsToAttributeSearchResults(items),
          total,
        };
      }),
      catchError(() => of({ results: [] as VersionAttributeSearchResult[], total: 0 })),
    );
  }

  private resolveAttributeFilterSearchUrl(baseUrl: string, entityType: VersionAssociationEntityType): string {
    switch (entityType) {
      case 'offer':
        return `${baseUrl}/GetVersionsByNotOfferIdWithAttributeFilter`;
      case 'promo':
        return `${baseUrl}/GetVersionsByNotPromoIdWithAttributeFilter`;
      case 'offerGroup':
        return `${baseUrl}/GetVersionsByNotOfferGroupIdWithAttributeFilter`;
    }
  }

  private resolveVersionCodeSearchUrl(baseUrl: string, entityType: VersionAssociationEntityType): string {
    switch (entityType) {
      case 'offer':
        return `${baseUrl}/GetVersionsByNotOfferId`;
      case 'promo':
        return `${baseUrl}/GetVersionsByNotPromoId`;
      case 'offerGroup':
        return `${baseUrl}/GetVersionsByNotOfferGroupId`;
    }
  }

  private buildVersionCodeSearchParams(
    context: VersionAssociationFilterContext,
    search: string,
  ): HttpParams {
    const fromObject: Record<string, string> = {
      eventId: context.eventId,
      $skip: '0',
      $top: '500',
      $orderby: 'VersionName asc',
      $count: 'true',
    };

    switch (context.entityType) {
      case 'offer':
        fromObject['offerId'] = context.entityId;
        break;
      case 'promo':
        fromObject['promoId'] = context.entityId;
        break;
      case 'offerGroup':
        fromObject['offerGroupId'] = context.entityId;
        break;
    }

    let params = new HttpParams({ fromObject });

    if (search.length) {
      params = params.append('$search', `"${search}"`);
    }

    return params;
  }

  private buildRequestBody(
    context: VersionAssociationFilterContext,
    attributeFilters: VersionAttributeFilterPair[],
    versionCodeSearch: string,
  ): Record<string, unknown> {
    const filterModel = {
      search: versionCodeSearch ?? '',
      skip: 0,
      limit: 500,
      orderDirection: 'asc',
      orderBy: 'VersionName',
    };

    const attributeFilterPayload = attributeFilters.map((filter) => ({
      attributeName: filter.attributeName,
      valueSourceField: filter.valueSourceField,
      value: filter.value,
    }));

    const body: Record<string, unknown> = {
      eventId: context.eventId,
      filterModel,
      attributeFilters: attributeFilterPayload,
    };

    switch (context.entityType) {
      case 'offer':
        body['offerId'] = context.entityId;
        break;
      case 'promo':
        body['promoId'] = context.entityId;
        break;
      case 'offerGroup':
        body['offerGroupId'] = context.entityId;
        break;
    }

    return body;
  }
}
